'use client';

import {useEffect, useState} from 'react';

import {getContentPreviewMode} from '@/lib/content-preview-mode';

interface UseCmsPreviewContentOptions<TVariables, TQuery, TData> {
    fetcher: (variables: TVariables & {isContentPreviewEnabled: true}) => () => Promise<TQuery>;
    variables: TVariables;
    adapt: (result: TQuery | null | undefined) => TData | null;
}

/**
 * Renders the SSR content by default. If the CMS team's `?preview_mode=true`
 * is present, fetches unpublished preview content client-side and swaps it
 * in — mirrors v1's per-component preview hooks, generalized across any
 * dynamicWebContent-backed section.
 */
export function useCmsPreviewContent<TVariables, TQuery, TData>(
    initialData: TData,
    {fetcher, variables, adapt}: UseCmsPreviewContentOptions<TVariables, TQuery, TData>,
): TData {
    const [data, setData] = useState(initialData);

    useEffect(() => {
        if (!getContentPreviewMode()) return;

        let cancelled = false;

        const fetchPreviewData = async () => {
            try {
                const result = await fetcher({...variables, isContentPreviewEnabled: true})();
                const adapted = adapt(result);
                if (!cancelled && adapted) {
                    setData(adapted);
                }
            } catch {
                // Preview fetch failed — keep rendering the already-rendered SSR content.
            }
        };

        fetchPreviewData();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return data;
}
